import { useState } from 'react';
import { mormSigner, AddressResult } from '../sdk';

/**
 * Custom hook for managing address generation from mnemonic phrases
 * 
 * @param initialMnemonic - Initial mnemonic phrase value (default: standard test mnemonic)
 * @param initialIndex - Initial address index value (default: 0)
 * @returns Object containing:
 *   - mnemonic: Current mnemonic phrase
 *   - setMnemonic: Function to update mnemonic phrase
 *   - addressIndex: Current address index
 *   - setAddressIndex: Function to update address index
 *   - result: Generated address result or error
 *   - loading: Boolean indicating if address generation is in progress
 *   - generateAddress: Function to trigger address generation
 *   - isReady: Boolean indicating if mormSigner is ready
 */
export function useAddressGenerator(
  initialMnemonic: string = 'abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon about',
  initialIndex: number = 0
) {
  const [mnemonic, setMnemonic] = useState<string>(initialMnemonic);
  const [addressIndex, setAddressIndex] = useState<number>(initialIndex);
  const [result, setResult] = useState<AddressResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const generateAddress = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: 'WASM module not ready'
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const result = await mormSigner.generateAddress(mnemonic, addressIndex);
      setResult(result);
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    mnemonic,
    setMnemonic,
    addressIndex,
    setAddressIndex,
    result,
    loading,
    generateAddress,
    isReady: mormSigner.isReady()
  };
}
